import { getTicketSystemConfigSilently } from '../commands/ticketsCommands';
import { removeTokenDetails, setTokenDetails, unlockSession } from '../commands/userCommands';
import { ACCA_SYSTEM_BET_ID, CASHOUT_OFFER_REJECTED_VALUE_UPDATE } from '../data-types/betting/bettingConstants';
import { EW, N_A } from '../data-types/constants';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { CASINO_ACTION_TYPE } from '../data-types/initialisation/initialisationEnums';
import { CommandResponseError, SocketMessage } from '../data-types/initialisation/initialisationInterfaces';
import { AcceptedCountries } from '../data-types/international/internationalEnums';
import { DialogType, NotificationTypes } from '../data-types/notification/notificationEnums';
import { NotificationPayload, QueuedNotification } from '../data-types/notification/notificationInterfaces';
import { POST_HOG_EVENT } from '../data-types/posthog/posthogEnums';
import { ReferralDetails } from '../data-types/referral/referralInterfaces';
import { SIGNUP_TEMPLATE } from '../data-types/signup/signupEnums';
import { ticketStatuses } from '../data-types/sports/sportsConstants';
import { STAGE } from '../data-types/sports/sportsEnums';
import { EventItem, RaceDetails, Ticket } from '../data-types/sports/sportsInterfaces';
import { SystemBetPlacementStatus, TicketType } from '../data-types/ticket/ticketEnums';
import { DetailsWebResponseData } from '../data-types/user/userInterfaces';
import {
  CardType,
  DEPOSIT_CONFIRMATION_TYPE,
  DEPOSIT_TYPE,
  PAYMENT_METHODS_TRANSACTION_TYPE_ID,
  WALLET_UPDATE_TYPE,
} from '../data-types/wallet/walletEnums';
import { UserBonusWallet } from '../data-types/wallet/walletInterfaces';
import { areSystemBetsAvailable } from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { trackSportradarPlaceBet } from '../services/sportradarPixel';
import { trackDeposit, trackLoginWithCredentials } from '../services/trackers';
import { setPlacingBet, updatePendingBets } from '../state/actions/betActions';
import { setCasinoNetPosition } from '../state/actions/casinoActions';
import { enqueueDialog, enqueueNotification, setErrorDialog, setRealityCheckDialog } from '../state/actions/dialogActions';
import { setIncomeAccessDepositTracking, setPaymentIframeDialogSource } from '../state/actions/iframeActions';
import { setFromAppFlag } from '../state/actions/loginActions';
import { setIsAfterZimplerSignup } from '../state/actions/signupActions';
import { setDetailedEvent, setRace } from '../state/actions/sportsbookActions';
import { deleteSportsbookEventItem, updateSportsbookEventItem } from '../state/actions/sportsbookEventsActions';
import {
  addFailedPlacedSystemBets,
  addSuccessPlacedSystemBets,
  enqueueBoostedSystemBetTicket,
  enqueueTicketForFullScreenPresentation,
  setCashoutOfferAccepted,
  setCashoutOfferRejected,
  setCashoutOfferUnavailable,
  setCashoutOfferUpdate,
  updatePendingTicketStatuses,
} from '../state/actions/ticketActions';
import { setAuthentication, setUserDetails } from '../state/actions/userDataActions';
import { setDepositCardType, setUserBalance, setUserBonusWallet, setUserTotalBalance } from '../state/actions/walletActions';
import { store } from '../state/store';
import { handlePlaceNextSystemBet } from './betslip';
import { tryEnqueueDepositNotificationAndResetDepositInfo } from './notifications';
import { loadPaymentMethods } from './paymentMethods';
import { postHogCapture } from './posthog';
import { handleSignupDynamicEndpointRequest, postSuccessSignupActions } from './signup';
import { convertRaceToEvent } from './sports';
import { eventExistsInMap } from './sportsbook';
import { shouldDisplayKwiffedDialog } from './ticket';

export const handleWalletUpdateBroadcast = (balance: number, sourceId: WALLET_UPDATE_TYPE, transactionId: number | undefined) => {
  const state = store.getState();
  const {
    wallet: { depositInfo, bonusWallet, balance: balanceBefore },
    userData: { user },
  } = state;
  const { signupV3 } = state.signup;
  const { depositConfirmationType, cardType, appNotification } = depositInfo;
  const { checkpoint } = signupV3;
  const depositedAmount = parseFloat((balance - balanceBefore).toFixed(2));
  const bonusAmount = bonusWallet ? bonusWallet.balance : 0;
  const totalBalanceAvailable = balance + bonusAmount;

  setUserBalance(balance);
  setUserTotalBalance(totalBalanceAvailable);
  setPaymentIframeDialogSource(undefined);

  if (
    checkpoint &&
    checkpoint.template === SIGNUP_TEMPLATE.FIRST_DEPOSIT &&
    balanceBefore < balance &&
    sourceId === WALLET_UPDATE_TYPE.DEPOSIT
  ) {
    handleSignupDynamicEndpointRequest(checkpoint);
    trackDeposit(depositedAmount, transactionId);
    postHogCapture(POST_HOG_EVENT.SUCCESS_FIRST_DEPOSIT, {});
    loadPaymentMethods(false, PAYMENT_METHODS_TRANSACTION_TYPE_ID.DEPOSIT);
  } else if (browserHistory.location.pathname === PATHS.SIGNUP.STEPS.DEPOSIT) {
    // legacy signup flow first deposit
    postSuccessSignupActions();

    if (balanceBefore < balance) {
      trackDeposit(depositedAmount, transactionId);
    }
    postHogCapture(POST_HOG_EVENT.FIRST_DEPOSIT_SUCCESS_LEGACY, {});
    loadPaymentMethods(false, PAYMENT_METHODS_TRANSACTION_TYPE_ID.DEPOSIT);
  } else if (
    sourceId === WALLET_UPDATE_TYPE.DEPOSIT &&
    balanceBefore < balance &&
    depositConfirmationType !== DEPOSIT_CONFIRMATION_TYPE.WITHDRAWAL
  ) {
    loadPaymentMethods(false, PAYMENT_METHODS_TRANSACTION_TYPE_ID.DEPOSIT);
    trackDeposit(depositedAmount, transactionId);

    if (cardType === CardType.DEPOSIT && depositConfirmationType === DEPOSIT_CONFIRMATION_TYPE.THANK_YOU_CARD) {
      setDepositCardType(CardType.THANK_YOU);
    } else if (appNotification.title && appNotification.content) {
      tryEnqueueDepositNotificationAndResetDepositInfo(depositConfirmationType, appNotification.title, appNotification.content);
    }

    if (user && !user.firstDepositAt) {
      postHogCapture(POST_HOG_EVENT.SUCCESS_FIRST_DEPOSIT, {});
    }
  }
};

export const handleTicketAcceptedBroadcast = (ticket: Ticket) => {
  const {
    userData: { userLocation },
    tickets: { pendingTickets, queuedBoostedSystemBetTickets },
  } = store.getState();

  if (shouldDisplayKwiffedDialog(ticket, pendingTickets)) {
    const systemBetsAreAvailable = areSystemBetsAvailable(userLocation || AcceptedCountries.GREAT_BRITAIN);
    const multipleSystemBetsBoosted = queuedBoostedSystemBetTickets.length > 0;

    if (systemBetsAreAvailable && multipleSystemBetsBoosted && !!ticket.accumulator) {
      enqueueBoostedSystemBetTicket(ticket);
    } else if (systemBetsAreAvailable && ticket.systemBetTypeId !== null) {
      enqueueBoostedSystemBetTicket(ticket);
    } else {
      enqueueTicketForFullScreenPresentation({
        ticket,
        ticketType: TicketType.KWIFFED,
      });
    }
  }

  if (ticket.systemBetTypeId || ticket.accumulator) {
    addSuccessPlacedSystemBets({
      systemBetId: ticket.systemBetTypeId || ACCA_SYSTEM_BET_ID,
      placementStatus: SystemBetPlacementStatus.SUCCESS,
    });
    handlePlaceNextSystemBet();
  }

  updatePendingTicketStatuses(ticket.id, ticketStatuses.ACCEPTED);

  const stake = ticket.amount;
  if (ticket.systemBetTypeId !== null) {
    postHogCapture(POST_HOG_EVENT.PLACED_MULTI_BET, { stake, betsNumber: ticket.totalNumberOfBets || N_A });
  } else if (ticket.accumulator) {
    postHogCapture(POST_HOG_EVENT.PLACED_ACCA_BET, { stake, betsNumber: ticket.bets.length });
  } else {
    postHogCapture(POST_HOG_EVENT.PLACED_SINGLE_BET, { stake });
  }
};

export const handleTicketBoostBroadcast = (ticket: Ticket) => {
  enqueueTicketForFullScreenPresentation({
    ticket,
    ticketType: TicketType.KWIFFED,
  });

  updatePendingTicketStatuses(ticket.id, ticketStatuses.ACCEPTED);
  setPlacingBet(false);
};

export const handleTicketAcceptedFreeTicketBroadcast = (ticket: Ticket, referrer: ReferralDetails) => {
  getTicketSystemConfigSilently();
  enqueueTicketForFullScreenPresentation({
    ticket,
    referrer,
    ticketType: TicketType.SURPRISE_BET,
  });
};

export const handleTicketRejectedBroadcast = (ticket: Ticket, error: CommandResponseError) => {
  updatePendingTicketStatuses(ticket.id, ticketStatuses.REJECTED, {
    errorMessage: error.notification.userFriendlyMessage,
    errorTitle: error.notification.userFriendlyTitle,
  });

  if (ticket.systemBetTypeId || ticket.accumulator) {
    addFailedPlacedSystemBets({
      systemBetId: ticket.systemBetTypeId || ACCA_SYSTEM_BET_ID,
      isEachWay: ticket.type === EW,
      totalAmount: ticket.amount,
      placementStatus: SystemBetPlacementStatus.FAILURE,
    });
  }
  handlePlaceNextSystemBet();
};

export const handleTicketCancelReasonBroadcast = (ticket: Ticket, message: string) => {
  updatePendingTicketStatuses(ticket.id, ticketStatuses.CANCELLED, { errorMessage: message });
  handlePlaceNextSystemBet();
};

export const handleUserRealityCheckBroadcast = (realityCheckInterval: number) => {
  setUserDetails({ realityCheckInterval });
  setRealityCheckDialog(true);
};

export const handleSessionLogoutBroadcast = () => {
  removeTokenDetails();
  setAuthentication(auth.UNAUTHENTICATED);
  setFromAppFlag(false);

  browserHistory.push(PATHS.APP.ROOT);
};

export const handleCashoutOfferUpdateBroadcast = (ticketId: number, cashoutOffer: number | null) => {
  setCashoutOfferUnavailable(ticketId, false);
  setCashoutOfferUpdate(ticketId, cashoutOffer);
};

export const handleCashoutAcceptedBroadcast = (
  ticketId: number,
  cashoutOffer: number,
  stake: number,
  boostedCashoutOffer: number | null | undefined,
) => {
  setCashoutOfferAccepted(ticketId, boostedCashoutOffer || null, true);

  if (boostedCashoutOffer) {
    enqueueTicketForFullScreenPresentation({
      stake,
      cashoutOffer,
      boostedCashoutOffer,
      ticketType: TicketType.CASHOUT,
    });
  }
};

export const handleCashoutRejectedBroadcast = (ticketId: number, cashoutOffer: number | null | undefined, error: CommandResponseError) => {
  if (cashoutOffer && typeof cashoutOffer === 'number' && typeof error === 'string' && error === CASHOUT_OFFER_REJECTED_VALUE_UPDATE) {
    setCashoutOfferUnavailable(ticketId, false);
    setCashoutOfferUpdate(ticketId, cashoutOffer);
  } else if (!cashoutOffer || typeof cashoutOffer !== 'number') {
    setCashoutOfferUnavailable(ticketId, true);
  } else {
    setCashoutOfferRejected(ticketId, true);
  }
};

export const handleEventBroadcast = (data: SocketMessage): void => {
  const {
    sportsbook,
    sportsbookEvents,
    bets: { pendingBets },
  } = store.getState();

  const {
    payload: { event, race },
  } = data;

  const updatedEvent = event ? event : race ? convertRaceToEvent(race) : undefined;
  if (pendingBets.length) {
    updatePendingBets(updatedEvent);
  }

  if (updatedEvent && eventExistsInMap(updatedEvent, sportsbookEvents)) {
    const { stage, sportId, id } = updatedEvent;
    if (stage === STAGE.FINISHED) {
      deleteSportsbookEventItem({ sportId, eventId: id });
    } else {
      updateSportsbookEventItem(updatedEvent);
    }
  }

  if (updatedEvent && sportsbook.detailedEvent && sportsbook.detailedEvent.id === updatedEvent.id) {
    setDetailedEvent(updatedEvent);

    if (sportsbook.race && sportsbook.race.id === updatedEvent.id) {
      updateRaceProps(sportsbook.race, updatedEvent);
    }
  }
};

const updateRaceProps = (currentRace: RaceDetails, detailedEvent: EventItem) => {
  setRace({
    ...currentRace,
    offers: [
      {
        ...currentRace.offers[0],
        outcomes:
          detailedEvent.raceOffers && detailedEvent.raceOffers.length
            ? detailedEvent.raceOffers[0].outcomes
            : currentRace.offers[0].outcomes,
      },
    ],
    bettingStatus: detailedEvent.bettingStatus,
    status: detailedEvent.status,
    disabled: detailedEvent.disabled,
    suspended: !!detailedEvent.suspended,
  });
};

export const handleInAppNotification = (payload: NotificationPayload) => {
  switch (payload.type) {
    case NotificationTypes.MODAL:
      const element = payload.template[0].elements[0];
      const actionName = payload.template[0].actions[0];
      const action = payload.actions[actionName];
      enqueueDialog({ type: DialogType.MODAL_NOTIFICATION_DIALOG, content: { element, action } });
      break;
    case NotificationTypes.BANNER:
      enqueueNotification(fetchDataFromNotification(payload));
      break;
  }
};

export const fetchDataFromNotification = (notification: NotificationPayload): QueuedNotification => {
  let image: string | undefined;
  let title: string | undefined;
  let body: string | undefined;
  let background: string | undefined;
  let url: string | undefined;
  let actionKey = '';

  notification.template.forEach(template => {
    const element = template.elements[0];
    actionKey = template.actions ? template.actions[0] : actionKey;

    switch (template.type) {
      case 'image':
        image = element.content;
        break;
      case 'title':
        title = element.content;
        break;
      case 'body':
        body = element.content;
        break;
      case 'backgroundColor':
        background = element.content;
        break;
    }
  });
  url = actionKey && notification.actions[actionKey] ? notification.actions[actionKey].target : undefined;
  return { image, title, body, background, url };
};

export const handleNewUserDetails = (newUserDetails: DetailsWebResponseData) => {
  setUserDetails({ depositsDisabled: !newUserDetails.depositsEnabled, depositsEnabledAt: newUserDetails.depositsEnabledAt });
};

export const handleCasinoActionBroadcast = (amount: number, type: CASINO_ACTION_TYPE) => {
  const {
    casino: { casinoNetPosition },
    userData: { user },
  } = store.getState();
  let newCasinoNetPosition = casinoNetPosition;

  if ([CASINO_ACTION_TYPE.WIN, CASINO_ACTION_TYPE.ROOLBACK].includes(type)) {
    newCasinoNetPosition += amount;
  }
  if (type === CASINO_ACTION_TYPE.LOSE) {
    newCasinoNetPosition -= amount;
    const pathname = window.location.pathname;
    const gameId = pathname.startsWith(PATHS.APP.CASINO.GAME) && pathname.split('/')[2];

    if (user && user.id && user.currency && gameId && !isNaN(Number(gameId))) {
      trackSportradarPlaceBet(user.id, amount, user.currency.isoAlphabeticCode, [`${gameId}`]);
    }
  }

  setCasinoNetPosition(newCasinoNetPosition);
};

export const handlePaymentDepositFailedBroadcast = (status: number) => {
  const {
    userData: { authentication, user },
    wallet: { depositInfo },
  } = store.getState();
  const { cardType, depositConfirmationType } = depositInfo;

  setIncomeAccessDepositTracking(false);
  if (authentication === auth.AUTHENTICATED) {
    if (cardType !== CardType.FAILED) {
      setDepositCardType(CardType.FAILED);
    }
    tryEnqueueDepositNotificationAndResetDepositInfo(depositConfirmationType, '', '');
  } else if (status && [DEPOSIT_TYPE.FAILED, DEPOSIT_TYPE.PENDING].includes(status as DEPOSIT_TYPE)) {
    setErrorDialog("Your deposit couldn't be accepted. Please try again.");
  }

  if (user && !user.firstDepositAt) {
    postHogCapture(POST_HOG_EVENT.FAILED_FIRST_DEPOSIT, {});
  }
};

export const handleSessionReUnlockBroadcast = async (token: string) => {
  setPaymentIframeDialogSource(undefined);
  setIncomeAccessDepositTracking(false);
  setTokenDetails(token);

  try {
    setIsAfterZimplerSignup(true);
    await unlockSession(token);
    trackLoginWithCredentials();
  } catch {
    // do nothing
  }
  if (browserHistory.location.pathname === PATHS.SIGNUP.ZIMPLER_DEPOSIT_CHOICE) {
    browserHistory.push(PATHS.APP.ROOT);
  }
};

export const handleZimplerDepositFailedBroadcast = () => {
  setPaymentIframeDialogSource(undefined);
  setIncomeAccessDepositTracking(false);
};

export const handleBonusWalletUpdateBroadcast = (bonusWallet: UserBonusWallet) => {
  const {
    wallet: { balance },
  } = store.getState();
  const { balance: bonusBalance } = bonusWallet;
  const totalBalance = balance + bonusBalance;

  setUserTotalBalance(totalBalance);
  setUserBonusWallet(bonusWallet);
};

export const handleBonusWalletRemoval = () => {
  const {
    wallet: { balance },
  } = store.getState();

  setUserTotalBalance(balance);
  setUserBonusWallet(undefined);
};
